import { mdiArrowRight } from '@mdi/js';
import Icon from '@mdi/react';
import { Box, Button, Typography } from '@mui/material';
import { Dispatch, SetStateAction, useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import Drawer from 'src/app/components/Drawer';

import { Colors } from '../../../shared/colors';
import { useAppSelector } from '../../../shared/hooks/redux';
import { paymentSelector } from '../../../shared/store/Pagamento/payment.selectors';
import { setTipo, setValor } from '../../../shared/store/Pagamento/payment.slice';
import { clearYunoPaymentSession } from '../../../shared/store/Pagamento/yunoPayment.slice';
import Currency from '../../components/Currency';

import AdicionarSaldo from './AdicionarSaldo';
import { Pagamento } from './Pagamento/Pagamento';
import PagamentoEfetuado from './PagamentoEfetuado';
import PagamentoRecusado from './PagamentoRecusado';
import SelecionarTipoPagamento from './SelecionarTipoPagamento';

interface Props {
  open: boolean;
  onClose: () => void;
}

interface TabSelectorProps {
  activeStep: number;
  erroMsg: string | null;
  setActiveStep: Dispatch<SetStateAction<number>>;
  setErroMsg: Dispatch<SetStateAction<string | null>>;
  onCloseDrawer: () => void;
}

const TabSelector = ({
  activeStep,
  erroMsg,
  setActiveStep,
  setErroMsg,
  onCloseDrawer
}: TabSelectorProps) => {
  const dispatch = useDispatch();
  const { tipo: tipoPagamento, valor } = useAppSelector(paymentSelector);

  useEffect(() => {
    if (tipoPagamento === null) return;

    setActiveStep((prevActiveStep) => {
      if (prevActiveStep !== 1) return prevActiveStep;
      return 2;
    });
  }, [setActiveStep, tipoPagamento]);

  const adicionarSaldo = useCallback(
    (saldo: number | '') => {
      dispatch(setValor(saldo ? saldo : 0));
    },
    [dispatch]
  );

  const onPaymentFalied = useCallback(
    (message?: string) => {
      setErroMsg(message ?? null);
      setActiveStep(3);
    },
    [setActiveStep, setErroMsg]
  );
  const onRetryPayment = useCallback(() => {
    setErroMsg(null);
    dispatch(setTipo(null));
    dispatch(clearYunoPaymentSession());
    setActiveStep(1);
  }, [dispatch, setActiveStep, setErroMsg]);
  const onPaymentComplete = useCallback(() => setActiveStep(4), [setActiveStep]);

  if (activeStep === 0) return <AdicionarSaldo saldo={valor} adicionarSaldo={adicionarSaldo} />;

  if (activeStep === 1) return <SelecionarTipoPagamento onBack={() => setActiveStep(0)} />;

  if (activeStep === 2 && tipoPagamento !== null) {
    return (
      <Pagamento
        tipoPagamento={tipoPagamento}
        onPaymentCompleted={onPaymentComplete}
        onPaymentFailed={onPaymentFalied}
      />
    );
  }
  if (activeStep === 3)
    return <PagamentoRecusado onPaymentFailed={onRetryPayment} erroMsg={erroMsg} />;

  if (activeStep === 4) return <PagamentoEfetuado onPaymentCompleted={onCloseDrawer} />;
  return null;
};

const DrawerAdicionarSaldo = ({ open, onClose }: Props) => {
  const [activeStep, setActiveStep] = useState(0);
  const [erroMsg, setErroMsg] = useState<string | null>(null);
  const { valor } = useAppSelector(paymentSelector);

  const dispatch = useDispatch();

  const handleNext = useCallback(() => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  }, []);

  const onCloseDrawer = useCallback(() => {
    onClose();
    setActiveStep(0);
    dispatch(setValor(0));
    dispatch(setTipo(null));
  }, [dispatch, onClose]);

  useEffect(() => {
    if (open) dispatch(setValor(150));
  }, [dispatch, open]);

  useEffect(() => {
    return () => {
      setActiveStep(0);
      dispatch(setValor(0));
      dispatch(setTipo(null));
    };
  }, [dispatch]);

  return (
    <Drawer
      variant="persistent"
      title="Adicionar saldo"
      open={open}
      onClose={onCloseDrawer}
      onClick={(e) => e.stopPropagation()}
    >
      <Box
        p={3}
        flex={1}
        minHeight={0}
        display="flex"
        flexDirection="column"
        justifyContent="space-between"
      >
        <TabSelector
          key={open ? 'open' : 'closed'}
          activeStep={activeStep}
          erroMsg={erroMsg}
          setActiveStep={setActiveStep}
          setErroMsg={setErroMsg}
          onCloseDrawer={onCloseDrawer}
        />
        {activeStep === 0 && (
          <Box pt={2} mt="auto">
            <Button
              fullWidth
              variant="contained"
              onClick={handleNext}
              disabled={!valor || valor <= 0}
              sx={{
                height: 44,
                display: 'flex',
                justifyContent: 'space-between',
                textTransform: 'none'
              }}
            >
              <Box width={18} />
              <Typography fontSize={14} fontWeight={500}>
                Continuar com <Currency value={Number(valor) || 0} />
              </Typography>
              <Icon path={mdiArrowRight} size={0.75} color={Colors.white} />
            </Button>
          </Box>
        )}
      </Box>
    </Drawer>
  );
};

export default DrawerAdicionarSaldo;
